import {
    useAmuseBoostStore,
    useBetSliderStore,
    useConfigurationStore,
    useJackpotStore,
    useNavigationStore,
    useSpinButtonSectionStore,
} from '@stores'
import React, { useEffect, useRef, useState } from 'react'
import {
    ChangeOpacityComponents,
    EQLevelReachedMessage,
    GameUIMessageScope,
    GameUIMessageType,
    JackpotFreezeIndexMessage,
    JackpotLinkCoinUpdateMessage,
    JackpotPhase,
    JackpotStartSmallSuitMessage,
    JackpotStatisticsMessage,
    JackpotWinAnimationStartMessage,
    ShowSystemMessage,
    ViewportType,
} from 'game-ui-types'
import JackpotEQSection from './JackpotEQ/JackpotEQSection'
import {
    JackpotMode,
    JackpotMlmWithHistory,
    JackpotLinkWithHistory,
    cn,
    isTerminalMessage,
    hasFlag,
    useOpacityToggle,
} from '@amusnet/react-ui'
import { getBetValue } from '@utils/betOption'
import { gameAnalytics } from '@utils/gameAnalytics'

const JackpotSection: React.FC = () => {
    const gameType = useConfigurationStore((state) => state.gameType)
    const isLinkJackpotType = useJackpotStore((state) => state.isLinkJackpotType)
    const isEQJackpotType = useJackpotStore((state) => state.isEQJackpotType)
    const latestSuitEvent = useJackpotStore((state) => state.latestSuitEvent)
    const pickedSuits = useJackpotStore((state) => state.pickedSuits)
    const pickedGems = useJackpotStore((state) => state.pickedGems)
    const winningLevelIndex = useJackpotStore((state) => state.winningLevelIndex)
    const currency = useConfigurationStore((state) => state.currency)
    const portalUid = useConfigurationStore((state) => state.portalUid)
    const setWinningLevelIndex = useJackpotStore((state) => state.setWinningLevelIndex)
    const isHistoryEnabled = useJackpotStore((state) => state.isHistoryEnabled)
    const shouldDisableJackpotHistory = useConfigurationStore((state) => state.shouldDisableJackpotHistory)
    const isJackpotHistoryEnabled = !shouldDisableJackpotHistory && isHistoryEnabled

    const playerName = useConfigurationStore((state) => state.playerName)
    const setLatestSuitEvent = useJackpotStore((state) => state.setLatestSuitEvent)
    const setJackpotEQStatistics = useJackpotStore((state) => state.setJackpotEQStatistics)
    const setJackpotPhase = useJackpotStore((state) => state.setJackpotPhase)
    const setIsJackpotEQStarted = useJackpotStore((state) => state.setIsJackpotEQStarted)
    const resetPickedSuits = useJackpotStore((state) => state.resetPickedSuits)
    const setFreezeIndex = useJackpotStore((state) => state.setFreezeIndex)
    const setHasLeadElement = useJackpotStore((state) => state.setHasLeadElement)
    const setPickedGems = useJackpotStore((state) => state.setPickedGems)
    const setJackpotStatsCountdownToZeroDurationSeconds = useJackpotStore(
        (state) => state.setJackpotStatsCountdownToZeroDurationSeconds
    )
    const jackpotStatsCountdownToZeroDurationSeconds = useJackpotStore(
        (state) => state.jackpotStatsCountdownToZeroDurationSeconds
    )
    const mode = useJackpotStore((state) => state.mode)
    const setMode = useJackpotStore((state) => state.setMode)
    const historyToolTipDateSeparator = useJackpotStore((state) => state.toolTipDateSeparator)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)
    const currencyHideCentsOnlyInJackpotBoxes = useJackpotStore((state) => state.currencyHideCentsOnlyInJackpotBoxes)
    const hideCents = currencyHideCentsOnlyInJackpotBoxes || currencyHideCents

    // Portrait: gap between the top and bottom jackpot box of each column
    const jackpotBoxesStyles = useConfigurationStore((state) => state.getComponentConfig('JackpotBoxes').styles)
    const jackpotBoxesClassNames = { leftColumn: jackpotBoxesStyles, rightColumn: jackpotBoxesStyles }

    const isBetSectionOpened = useSpinButtonSectionStore((state) => state.isBetSectionOpened)
    const viewport = useConfigurationStore((state) => state.viewport)
    const stageDimensions = useConfigurationStore((state) => state.stageDimensions)

    const isAutoplaySectionOpened = useSpinButtonSectionStore((state) => state.isAutoplaySectionOpened)

    const isMenuOpen = useNavigationStore((state) => state.isMenuOpen)
    const isAmuseBoostModalOpen = useAmuseBoostStore((state) => state.isAmuseBoostModalOpen)
    const closeHistorySignal = useJackpotStore((state) => state.closeHistorySignal)

    const betOptions = useConfigurationStore((state) => state.betOptions)
    const activeIndex = useBetSliderStore((state) => state.activeIndex)

    const jackpotSectionComponent = useRef<HTMLDivElement | null>(null)
    const apiBaseUrl = useConfigurationStore((state) => state.apiBaseUrl)
    const [shouldForceCompleteWinAnimation, setShouldForceCompleteWinAnimation] = useState(false)
    const [isSessionActive, setIsSessionActive] = useState(true)

    const shouldHideJackpotWinnerNames = hasFlag('shouldHideJackpotWinnerNames')

    useOpacityToggle(ChangeOpacityComponents.JACKPOT_SECTION, jackpotSectionComponent)

    // listen for jackpot messages from game-engine
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const { type, scope } = rawData

            switch (type) {
                case GameUIMessageType.JACKPOT_STATISTICS: {
                    const data = rawData as JackpotStatisticsMessage

                    if (data.jackpotEQStatistics) {
                        setJackpotEQStatistics(data.jackpotEQStatistics)
                    }

                    break
                }
                case GameUIMessageType.JACKPOT_STARTED: {
                    setJackpotPhase(JackpotPhase.INTRO_STARTED)
                    break
                }
                case GameUIMessageType.JACKPOT_RUNNING: {
                    setShouldForceCompleteWinAnimation(false)
                    setJackpotPhase(JackpotPhase.RUNNING)
                    setMode(JackpotMode.STARTED)
                    break
                }
                case GameUIMessageType.JACKPOT_EQ_STARTED: {
                    setIsJackpotEQStarted(true)
                    break
                }
                case GameUIMessageType.JACKPOT_EQ_ENDED: {
                    setIsJackpotEQStarted(false)
                    resetPickedSuits()
                    setFreezeIndex(-1)
                    setWinningLevelIndex?.(undefined)
                    setJackpotPhase(JackpotPhase.CLOSED)
                    break
                }
                case GameUIMessageType.EQ_LEVEL_REACHED: {
                    const data = rawData as EQLevelReachedMessage

                    setFreezeIndex(data.levelIndex)
                    break
                }
                case GameUIMessageType.JACKPOT_FREEZE_INDEX: {
                    const data = rawData as JackpotFreezeIndexMessage

                    setFreezeIndex(data.freezeIndex)
                    break
                }
                case GameUIMessageType.JACKPOT_WIN_ANIMATION_START: {
                    const data = rawData as JackpotWinAnimationStartMessage
                    const winningIndex = useJackpotStore.getState().freezeIndex

                    setWinningLevelIndex?.(winningIndex === -1 ? undefined : winningIndex)

                    setJackpotStatsCountdownToZeroDurationSeconds(data.animationTime)
                    setJackpotPhase(JackpotPhase.OUTRO_STARTED)
                    break
                }
                case GameUIMessageType.JACKPOT_CLICKED_DURING_WIN_ANIMATION: {
                    setShouldForceCompleteWinAnimation(true)
                    setJackpotPhase(JackpotPhase.OUTRO_CLICKED)
                    break
                }
                case GameUIMessageType.JACKPOT_WIN_ANIMATION_COMPLETE: {
                    // End of results should behave like jackpot outro clicked phase
                    setJackpotPhase(JackpotPhase.OUTRO_CLICKED)

                    setWinningLevelIndex?.(undefined)

                    setMode(JackpotMode.ENDED)
                    break
                }
                case GameUIMessageType.JACKPOT_COMPLETE: {
                    setShouldForceCompleteWinAnimation(false)
                    resetPickedSuits()
                    setLatestSuitEvent(null)
                    setWinningLevelIndex?.(undefined)
                    setMode(JackpotMode.IDLE)
                    setJackpotPhase(JackpotPhase.CLOSED)
                    break
                }
                case GameUIMessageType.JACKPOT_START_SMALL_SUIT: {
                    const data = rawData as JackpotStartSmallSuitMessage

                    setLatestSuitEvent({
                        suit: data.suit,
                        sourceX: data.animationStartPoint.x,
                        sourceY: data.animationStartPoint.y,
                        id: crypto.randomUUID(),
                        gameType,
                    })

                    break
                }
                case GameUIMessageType.SHOW_SYSTEM_MESSAGE: {
                    const data = rawData as ShowSystemMessage

                    if (isTerminalMessage(data.messageType)) {
                        setIsSessionActive(false)
                    }

                    break
                }
                case 'jackpotLinkCoinUpdate': {
                    const { MAJOR, GRAND } = rawData as JackpotLinkCoinUpdateMessage

                    setPickedGems({ MAJOR, GRAND })

                    break
                }
            }

            if (scope !== GameUIMessageScope.JACKPOT && scope !== GameUIMessageScope.JACKPOT_CARDS) {
                return
            }

            switch (type) {
                case GameUIMessageType.JACKPOT_HAS_LEAD_ELEMENT: {
                    setHasLeadElement(true)
                    break
                }
            }
        }

        window.uiBus.addEventListener('message', handleMessage, false)

        return () => {
            window.uiBus.removeEventListener('message', handleMessage, false)
        }
    }, [])

    let jackpotSection

    if (isEQJackpotType) {
        jackpotSection = <JackpotEQSection />
    } else if (isLinkJackpotType) {
        jackpotSection = (
            <JackpotLinkWithHistory
                key={'jackpot-link-component'}
                apiBaseUrl={apiBaseUrl}
                portalUid={portalUid}
                currency={currency}
                betAmount={getBetValue(betOptions[activeIndex])}
                mode={mode}
                orientation={viewport}
                hideCents={hideCents}
                hidePlayerName={shouldHideJackpotWinnerNames}
                timings={{
                    remoteWinnerDisplayMs: 8000,
                }}
                gemsProgress={pickedGems}
                isSessionActive={isSessionActive}
                forceCloseHistory={closeHistorySignal}
                historyProps={{
                    apiBaseUrl,
                    portalUid,
                    currency,
                    viewport,
                    stageDimensions,
                    hideCents,
                    toolTipDateSeparator: historyToolTipDateSeparator || '.',
                    hidePlayerName: shouldHideJackpotWinnerNames,
                }}
                isHistoryEnabled={isJackpotHistoryEnabled}
            />
        )
    } else {
        jackpotSection = (
            <JackpotMlmWithHistory
                key={'jackpot-mlm-component'}
                apiBaseUrl={apiBaseUrl}
                classNames={jackpotBoxesClassNames}
                portalUid={portalUid}
                currency={currency}
                orientation={viewport}
                playerName={playerName}
                mode={mode}
                hideCents={hideCents}
                hidePlayerName={shouldHideJackpotWinnerNames}
                isMinimized={false}
                isSessionActive={isSessionActive}
                forceCloseHistory={closeHistorySignal}
                winningLevelIndex={winningLevelIndex}
                isLogoVisible={mode !== JackpotMode.IDLE}
                logoConfig={{
                    [ViewportType.MOBILE_PORTRAIT]: {
                        position: {
                            top: 73,
                            left: '50%',
                            transform: 'translateX(-50%)',
                        },
                    },
                }}
                areSmallSuitsVisible={mode !== JackpotMode.IDLE}
                timings={{
                    remoteWinnerDisplayMs: 8000,
                    localWinnerRestoreDelayMs: 5000,
                }}
                // The panel always opens on its Last-100 sort; it reports no later sort change.
                onHistoryOpen={(suit) => gameAnalytics.jackpotStatisticsOpened(suit, 'last')}
                smallSuitAnimationEvent={latestSuitEvent}
                smallSuitProgress={pickedSuits}
                countdownToZeroDuration={jackpotStatsCountdownToZeroDurationSeconds}
                forceCompleteWinAnimation={shouldForceCompleteWinAnimation && winningLevelIndex != null}
                historyProps={{
                    apiBaseUrl,
                    portalUid,
                    currency,
                    viewport,
                    stageDimensions,
                    hideCents,
                    toolTipDateSeparator: historyToolTipDateSeparator || '.',
                    hidePlayerName: shouldHideJackpotWinnerNames,
                }}
                isHistoryEnabled={isJackpotHistoryEnabled}
            />
        )
    }

    return (
        <div
            ref={jackpotSectionComponent}
            className={cn(
                'w-full absolute px-[0.5em]',
                viewport === ViewportType.MOBILE_PORTRAIT ? 'top-[5px]' : 'top-[0.5em]',
                isBetSectionOpened || isAutoplaySectionOpened || isMenuOpen || isAmuseBoostModalOpen
                    ? 'z-[0]'
                    : 'z-[202]'
            )}
        >
            {jackpotSection}
        </div>
    )
}

export default JackpotSection
